/**
 * Everything the Team page reads, in one hook (ADR-0044, PR 2 Task 7).
 *
 * Six reads, one refetch. They are separate endpoints because they are
 * separate things a Team has, but they are one render: a page that showed the
 * lines before it knew their status would print "on" and then correct itself
 * to "needs setup", which is exactly the flicker that teaches a manager not to
 * trust the word.
 *
 * `GET /jobs` is what a write PATCHes against; `GET /jobs/status` is what the
 * page RENDERS. They are joined on the line's id and ordered independently, so
 * the join is by id and never by position.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  DeskAttachment,
  Job,
  JobKindWire,
  JobStatus,
  TeamAgentWire,
  TeamAutonomy,
  TeamRunLimits,
  TeamTicketOwners,
  TicketBudget,
} from "@yagni/shared";
import { isTeamAutonomy } from "@yagni/shared";

import { captureUiError } from "../sentry.js";
import type { BacklogView } from "./copy.js";
import {
  fetchTeamCatalog,
  fetchTeamJobStatus,
  fetchTeamJobs,
  fetchTeamMembers,
  type TeamAnswerers,
  type TeamJobGroup,
  type TeamMember,
  type TeamPaused,
  type TeamTracker,
} from "./teamsApi.js";

/**
 * One live Playbook rule, as `GET /api/desks/:id` carries it. The Team page
 * quotes a few of them on its Playbook card and the Playbook sheet edits them.
 */
export interface PlaybookRule {
  id: string;
  ruleText: string;
  origin: "human" | "proposed" | "platform";
  status: string;
  /** The "applies to…" routing tag (YAG-359); absent or null is unscoped. */
  scope?: { workflow?: string; role?: string; repo?: string; paths?: string[] } | null;
  /** `workspace` is an inherited org-floor rule this Team cannot edit. */
  tier?: "workspace" | "team";
  overridesRuleId?: string | null;
  overridesRuleText?: string | null;
}

/** The Team itself, as the header prints it. */
export interface TeamHeader {
  id: string;
  name: string;
  mission: string | null;
  visibility: string | null;
  archivedAt: string | null;
  /** The Team's owner, who may manage it without being an admin. */
  publisherUserId: string | null;
  /** Who reads the Team: on a restricted Team, who may leave it. */
  subscriberIds: string[];
  /** Personal (derived: its owner's tickets, and nobody else on it). Absent on older servers. */
  personal?: boolean;
  /** Reeve drafts a UI preview for plans that change what a person sees. On unless turned off. */
  uiPreviewEnabled: boolean;
  /** YAGNI merges the Team's green, Proctor-approved pull requests nobody objected to. Off unless turned on. */
  autoMerge?: boolean;
  /** The Team ladder step a person picked; null until someone picks one (the lines then say it). */
  autonomy?: TeamAutonomy | null;
  /** Who may edit the Team's charter beside its owner; null means the owner alone. */
  editorUserId?: string | null;
  /** How far one build or one plan may go, resolved over the defaults. Absent on older servers. */
  runLimits?: TeamRunLimits | null;
  /** The one budget a ticket gets, resolved (decision 6, 2026-10-19). Absent on older servers. */
  ticketBudget?: TicketBudget | null;
  /** Automatic turns a day; null is the default. */
  dailyTurnBudget?: number | null;
  /** Today's draw on those turns, read only by the Team's owner, editor or an admin. */
  automaticTurnUsage?: { used: number; remaining: number; limit: number } | null;
  /** The declared system the Team builds inside (YAG-1108); null when it picks none. */
  declaredSystemId: string | null;
  declaredSystemName: string | null;
  /**
   * The system with its members (YAG-1109), for the block under the header.
   * Null when the Team picks none; absent from an older server.
   */
  declaredSystem?: TeamDeclaredSystem | null;
}

/** A Team's declared system as the Team page lists it. */
export interface TeamDeclaredSystem {
  id: string;
  name: string;
  revision: number;
  repos: { fullName: string; shared: boolean; compose: boolean }[];
}

/** Reads the desk's `declaredSystem`; anything malformed reads as absent. */
export function readDeclaredSystem(value: unknown): TeamDeclaredSystem | null | undefined {
  if (value === undefined) return undefined;
  if (!isRecord(value)) return null;
  if (typeof value.id !== "string" || typeof value.name !== "string") return null;
  const repos = Array.isArray(value.repos) ? value.repos : [];
  return {
    id: value.id,
    name: value.name,
    revision: typeof value.revision === "number" ? value.revision : 0,
    repos: repos
      .filter((repo): repo is Record<string, unknown> => isRecord(repo))
      .filter((repo) => typeof repo.fullName === "string")
      .map((repo) => ({
        fullName: repo.fullName as string,
        shared: repo.shared === true,
        compose: repo.compose === true,
      })),
  };
}

export interface TeamPageState {
  team: TeamHeader | null;
  groups: TeamJobGroup[];
  /** Every line flat, for the Add picker's arithmetic. */
  jobs: Job[];
  statusById: Map<string, JobStatus>;
  kindsById: Map<string, JobKindWire>;
  agentsBySlug: Map<string, TeamAgentWire>;
  agents: TeamAgentWire[];
  kinds: JobKindWire[];
  members: TeamMember[];
  answerers: TeamAnswerers;
  /** Whose tracker tickets this Team works from; null is anyone's. */
  ticketsAssignedTo: TeamTicketOwners;
  /** The workspace's connected trackers, for "my Jira tickets". */
  trackers: TeamTracker[];
  paused: TeamPaused;
  attachments: DeskAttachment[];
  /** Slack (and other) channels this Team posts to, by id. */
  channels: Array<{ channelId: string; label: string }>;
  /** Published repository environments, for the environment blanks. */
  environments: string[];
  /** The live Playbook. Null when the Team's own read did not land. */
  playbookRules: PlaybookRule[] | null;
  /** Open tickets on this Team, for "Work · N". Null when unread. */
  workCount: number | null;
  /**
   * This month's Agent spend on this Team, sell side, in millicents. Null when
   * the reader may not see spend (Usage is admin-gated) or the read missed.
   */
  spendMillicents: number | null;
  isLoading: boolean;
  error: string | null;
  notFound: boolean;
  refetch: () => Promise<void>;
}

const NO_PAUSE: TeamPaused = {
  paused: false,
  pausedAt: null,
  pausedByUserId: null,
  pausedByName: null,
};

/**
 * A field of the Team read: absent (an older server) reads as null quietly; a
 * value that is there but does not parse also reads as null, so the sheet
 * falls back to the defaults, but it is reported, because the sheet would
 * otherwise show $25 on a Team someone raised to $60 with no trace of why.
 */
function readField<T>(
  value: unknown,
  parse: (value: unknown) => value is T,
  field: string,
  deskId: string,
): T | null {
  if (value === undefined || value === null) return null;
  if (parse(value)) return value;
  captureUiError(new Error(`Team read carried an unreadable ${field}`), {
    area: "Teams",
    operation: "team_read_parse",
    severity: "warning",
    deskId,
    // Config numbers, not personal data; capped so a runaway payload stays small.
    received: receivedSample(value),
  });
  return null;
}

const RECEIVED_SAMPLE_MAX = 200;

/** The unreadable value as JSON, at most 200 characters, for the report. */
export function receivedSample(value: unknown): string {
  let text: string;
  try {
    text = JSON.stringify(value) ?? String(value);
  } catch {
    text = String(value);
  }
  return text.length > RECEIVED_SAMPLE_MAX ? `${text.slice(0, RECEIVED_SAMPLE_MAX)}…` : text;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object";
}

function isRunLimits(value: unknown): value is TeamRunLimits {
  const stage = (entry: unknown) =>
    isRecord(entry) &&
    ["maxRequests", "maxCostMillicents", "maxElapsedMs"].every(
      (key) => typeof entry[key] === "number",
    );
  return isRecord(value) && stage(value.planning) && stage(value.development);
}

function isTicketBudget(value: unknown): value is TicketBudget {
  return (
    isRecord(value) &&
    typeof value.maxCostMillicents === "number" &&
    typeof value.maxElapsedMs === "number"
  );
}

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value);
}

function isTurnUsage(value: unknown): value is { used: number; remaining: number; limit: number } {
  return (
    isRecord(value) &&
    typeof value.used === "number" &&
    typeof value.remaining === "number" &&
    typeof value.limit === "number"
  );
}

/** Best-effort side reads. A Team page still renders when one of these misses. */
async function quietGet<T>(url: string, fallback: T, read: (body: unknown) => T): Promise<T> {
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return fallback;
    return read(await res.json());
  } catch {
    return fallback;
  }
}

export function useTeamPage(deskId: string | null): TeamPageState {
  const [team, setTeam] = useState<TeamHeader | null>(null);
  const [groups, setGroups] = useState<TeamJobGroup[]>([]);
  const [statuses, setStatuses] = useState<JobStatus[]>([]);
  const [kinds, setKinds] = useState<JobKindWire[]>([]);
  const [agents, setAgents] = useState<TeamAgentWire[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [answerers, setAnswerers] = useState<TeamAnswerers>({});
  const [ticketsAssignedTo, setTicketsAssignedTo] = useState<TeamTicketOwners>(null);
  const [trackers, setTrackers] = useState<TeamTracker[]>([]);
  const [paused, setPaused] = useState<TeamPaused>(NO_PAUSE);
  const [attachments, setAttachments] = useState<DeskAttachment[]>([]);
  const [channels, setChannels] = useState<Array<{ channelId: string; label: string }>>([]);
  const [environments, setEnvironments] = useState<string[]>([]);
  const [playbookRules, setPlaybookRules] = useState<PlaybookRule[] | null>(null);
  const [workCount, setWorkCount] = useState<number | null>(null);
  const [spendMillicents, setSpendMillicents] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const refetch = useCallback(async () => {
    if (!deskId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const [jobsBody, statusBody, catalogBody, membersBody] = await Promise.all([
        fetchTeamJobs(deskId),
        fetchTeamJobStatus(deskId),
        fetchTeamCatalog(deskId),
        fetchTeamMembers(deskId),
      ]);
      setGroups(jobsBody.engagements);
      setPaused(jobsBody.paused ?? NO_PAUSE);
      setStatuses(statusBody);
      setKinds(catalogBody.kinds);
      setAgents(catalogBody.agents);
      setMembers(membersBody.members);
      setAnswerers(membersBody.answerers ?? {});
      setTicketsAssignedTo(membersBody.ticketsAssignedTo ?? null);
      setTrackers(membersBody.trackers ?? []);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 404) setNotFound(true);
      else setError((err as Error).message);
      setIsLoading(false);
      return;
    }

    // The scope sentence's facts. Each is its own long-standing endpoint and
    // none of them can fail the page: a missing channel list prints "nowhere
    // yet", which is the truth a manager needs anyway.
    const base = `/api/desks/${encodeURIComponent(deskId)}`;
    const [desk, attached, targets, envs, openTickets, spend] = await Promise.all([
      quietGet<(TeamHeader & { playbookRules: PlaybookRule[] }) | null>(base, null, (body) => {
        const row = (body as { desk?: Record<string, unknown> }).desk ?? body;
        if (!row || typeof row !== "object") return null;
        const record = row as Record<string, unknown>;
        const subscribers = (body as { subscribers?: unknown }).subscribers;
        return {
          playbookRules: Array.isArray(record.playbookRules)
            ? (record.playbookRules as PlaybookRule[])
            : [],
          id: String(record.id ?? deskId),
          name: String(record.name ?? ""),
          mission: typeof record.mission === "string" ? record.mission : null,
          visibility: typeof record.visibility === "string" ? record.visibility : null,
          archivedAt: typeof record.archivedAt === "string" ? record.archivedAt : null,
          publisherUserId:
            typeof record.publisherUserId === "string" ? record.publisherUserId : null,
          personal: record.personal === true,
          uiPreviewEnabled: record.uiPreviewEnabled !== false,
          autoMerge: record.autoMerge === true,
          autonomy: isTeamAutonomy(record.autonomy) ? record.autonomy : null,
          editorUserId: typeof record.editorUserId === "string" ? record.editorUserId : null,
          declaredSystemId:
            typeof record.declaredSystemId === "string" ? record.declaredSystemId : null,
          declaredSystemName:
            typeof record.declaredSystemName === "string" ? record.declaredSystemName : null,
          declaredSystem: readDeclaredSystem(record.declaredSystem),
          runLimits: readField(record.runLimits, isRunLimits, "runLimits", deskId),
          ticketBudget: readField(record.ticketBudget, isTicketBudget, "ticketBudget", deskId),
          dailyTurnBudget: readField(record.dailyTurnBudget, isCount, "dailyTurnBudget", deskId),
          automaticTurnUsage: readField(
            record.automaticTurnUsage,
            isTurnUsage,
            "automaticTurnUsage",
            deskId,
          ),
          subscriberIds: Array.isArray(subscribers)
            ? subscribers.flatMap((entry) =>
                typeof (entry as { userId?: unknown })?.userId === "string"
                  ? [(entry as { userId: string }).userId]
                  : [],
              )
            : [],
        };
      }),
      quietGet<DeskAttachment[]>(`${base}/attachments`, [], (body) =>
        Array.isArray(body)
          ? (body as DeskAttachment[])
          : ((body as { attachments?: DeskAttachment[] }).attachments ?? []),
      ),
      quietGet<Array<{ channelId: string; label: string }>>(
        `${base}/delivery-targets`,
        [],
        (body) => {
          const list = (body as { targets?: unknown }).targets;
          if (!Array.isArray(list)) return [];
          return list
            .filter(
              (entry): entry is { kind: string; channelId: string; label?: string } =>
                typeof entry === "object" &&
                entry !== null &&
                typeof (entry as { channelId?: unknown }).channelId === "string",
            )
            .map((entry) => ({
              channelId: entry.channelId,
              label: entry.label ?? entry.channelId,
            }));
        },
      ),
      quietGet<string[]>(`${base}/repository-environments`, [], (body) => {
        const list = (body as { environments?: unknown }).environments;
        if (!Array.isArray(list)) return [];
        return list
          .map((entry) =>
            typeof entry === "string"
              ? entry
              : typeof (entry as { name?: unknown })?.name === "string"
                ? String((entry as { name: string }).name)
                : null,
          )
          .filter((entry): entry is string => entry !== null);
      }),
      quietGet<number | null>(
        `/api/work-items?deskId=${encodeURIComponent(deskId)}&excludeDone=true`,
        null,
        (body) => {
          const list = Array.isArray(body) ? body : (body as { tickets?: unknown }).tickets;
          return Array.isArray(list) ? list.length : null;
        },
      ),
      quietGet<number | null>(monthSpendUrl(new Date()), null, (body) =>
        teamSpendFrom(body, deskId),
      ),
    ]);
    setTeam(desk);
    setAttachments(attached);
    setChannels(targets);
    setPlaybookRules(desk?.playbookRules ?? null);
    setWorkCount(openTickets);
    setSpendMillicents(spend);
    setEnvironments(envs);
    setIsLoading(false);
  }, [deskId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const jobs = useMemo(() => groups.flatMap((group) => group.jobs), [groups]);
  const statusById = useMemo(
    () => new Map(statuses.map((status) => [status.jobId, status])),
    [statuses],
  );
  const kindsById = useMemo(() => new Map(kinds.map((kind) => [kind.kind, kind])), [kinds]);
  const agentsBySlug = useMemo(
    () => new Map(agents.map((agent) => [agent.personaSlug, agent])),
    [agents],
  );

  return {
    team,
    groups,
    jobs,
    statusById,
    kindsById,
    agentsBySlug,
    agents,
    kinds,
    members,
    answerers,
    ticketsAssignedTo,
    trackers,
    paused,
    attachments,
    channels,
    environments,
    playbookRules,
    workCount,
    spendMillicents,
    isLoading,
    error,
    notFound,
    refetch,
  };
}

/** `YYYY-MM-DD` in UTC, the day grain the usage routes read. */
function utcDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * This month's Agent spend, month to date: the first of the month (UTC) up to
 * and including today, which the usage route reads as `[from, to)`.
 */
export function monthSpendUrl(now: Date): string {
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
  return `/api/yagni-code-usage/workers?from=${utcDay(from)}&to=${utcDay(to)}`;
}

/**
 * The Agents' spend on ONE Team, from the workspace rollup's per-line rows.
 * A run with no line behind it is not attributed to any Team, so this can sum
 * to less than the Team's true cost; it never sums to more.
 */
export function teamSpendFrom(body: unknown, deskId: string): number | null {
  const report = (body as { workers?: { workers?: unknown } } | null)?.workers;
  const rows = report?.workers;
  if (!Array.isArray(rows)) return null;
  let total = 0;
  for (const row of rows as Array<{ jobs?: unknown }>) {
    if (!Array.isArray(row.jobs)) continue;
    for (const line of row.jobs as Array<{ deskId?: unknown; sellMillicents?: unknown }>) {
      if (line.deskId === deskId && typeof line.sellMillicents === "number") {
        total += line.sellMillicents;
      }
    }
  }
  return total;
}

/** The repositories the Team attaches, for the scope sentence and the pickers. */
export function attachedRepos(attachments: readonly DeskAttachment[]): string[] {
  return attachments.filter((entry) => entry.kind === "repo").map((entry) => entry.ref);
}

/** The backlogs the Team attaches: tracker views, projects and epics. */
export function attachedBacklogs(attachments: readonly DeskAttachment[]): BacklogView[] {
  return attachments
    .filter((entry) => entry.kind !== "repo")
    .map((entry) => ({
      ref: entry.ref,
      label: entry.label ?? entry.ref,
      source: entry.source,
      kind: entry.kind,
    }));
}
